import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/data/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-[60] transition-colors duration-500"
      style={{
        backgroundColor: scrolled ? "rgba(247,244,238,0.92)" : "rgba(247,244,238,0)",
        backdropFilter: scrolled ? "blur(14px)" : "none",
        borderBottom: scrolled ? "1px solid #E7E2D8" : "1px solid transparent",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center gap-3.5" data-testid="nav-logo-link" aria-label="Arun Gupta & Co. home">
            <img src="/brand/logo.png" alt="Arun Gupta & Co. Chartered Accountants logo" className="h-12 w-12 lg:h-14 lg:w-14 object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-navy text-lg sm:text-xl lg:text-2xl tracking-tight">Arun Gupta &amp; Co.</span>
              <span className="overline mt-1" style={{ fontSize: "0.55rem" }}>Chartered Accountants</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[0.82rem] font-medium tracking-wide transition-colors duration-300 link-underline ${
                    isActive ? "text-brass" : "text-charcoal/80 hover:text-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact" className="btn-line" data-testid="nav-get-in-touch-btn">
              Get in Touch
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
          </div>

          <button
            className="lg:hidden text-navy p-2 -mr-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open-btn"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            className="fixed inset-0 z-[100] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu-overlay"
          >
            <div className="absolute inset-0" style={{ background: "#F7F4EE" }} />
            <motion.div
              className="relative h-full flex flex-col px-6 sm:px-10 pt-8"
              initial={{ y: -18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -12, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between h-12">
                <div className="flex items-center gap-3">
                  <img src="/brand/logo.png" alt="Arun Gupta & Co. logo" className="h-11 w-11 object-contain" />
                  <span className="font-serif text-navy text-xl">Arun Gupta &amp; Co.</span>
                </div>
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-navy p-2 -mr-2" data-testid="mobile-menu-close-btn">
                  <X size={26} strokeWidth={1.5} />
                </button>
              </div>
              <div className="hairline mt-6" />
              <nav className="flex flex-col mt-4" aria-label="Mobile">
                {NAV.map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-baseline justify-between py-4 border-b border-border-subtle font-serif text-3xl ${
                          isActive ? "text-brass" : "text-navy"
                        }`
                      }
                    >
                      {item.label}
                      <span className="overline" style={{ fontSize: "0.55rem" }}>{String(i + 1).padStart(2, "0")}</span>
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-8">
                <Link to="/contact" className="btn-solid w-full justify-center" data-testid="mobile-get-in-touch-btn">
                  Get in Touch
                  <ArrowUpRight size={15} strokeWidth={1.5} />
                </Link>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </header>
  );
};

export default Navbar;
